import { DotsSixVerticalIcon } from '@phosphor-icons/react'
import { ArrowDown, ArrowUp, Copy, Plus, Trash, Xmark } from 'iconoir-react'
import { ItemTypes } from 'app/components/sortable-dnd/ItemTypes'
import { useSortableItem } from 'app/components/sortable-dnd/useSortableItem'
import { Field } from 'app/components/workflows/inspectors/fields'
import { Badge } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { Switch } from 'app/components/ui/switch'
import { Textarea } from 'app/components/ui/textarea'
import {
  changeKind,
  conditionSources,
  conditionValues,
  MAPPING_TARGET_LABELS,
  mappingTargetsFor,
  mintId,
  OPTION_KINDS,
  QUESTION_KIND_LABELS,
} from 'app/lib/forms/builder'
import type { FormQuestion, QuestionKind } from 'app/lib/forms/types'
import { NON_ANSWERABLE_KINDS } from 'app/lib/forms/types'
import { cn } from 'app/lib/utils'

type Props = {
  question: FormQuestion
  index: number
  questions: FormQuestion[]
  expanded: boolean
  readOnly: boolean
  onToggle: () => void
  onChange: (next: FormQuestion) => void
  onMove: (to: number) => void
  onReorder: (from: number, to: number) => void
  onDuplicate: () => void
  onRemove: () => void
}

const NO_MAPPING = `none`
const NO_CONDITION = `always`

export function QuestionCard({
  question,
  index,
  questions,
  expanded,
  readOnly,
  onToggle,
  onChange,
  onMove,
  onReorder,
  onDuplicate,
  onRemove,
}: Props) {
  const { rowRef, handleRef, isDragging, handlerId } = useSortableItem({
    type: ItemTypes.FORM_QUESTION,
    id: question.id,
    index,
    onMove: onReorder,
  })
  const sources = conditionSources(questions, index)
  const source = sources.find((q) => q.id === question.showWhen?.questionId)
  const answerable = !NON_ANSWERABLE_KINDS.includes(question.kind)
  const targets = mappingTargetsFor(question.kind)
  const summary = question.label.trim() || (question.kind === `staticText` ? `Empty text block` : `Untitled question`)
  const conditionLabel = source && conditionValues(source).find((v) => v.id === question.showWhen?.equals)?.label

  const patch = (change: Partial<FormQuestion>) => onChange({ ...question, ...change })
  const setOptions = (options: FormQuestion['options']) => patch({ options })

  return (
    <div
      ref={rowRef}
      data-handler-id={handlerId}
      className={cn(
        `rounded-xl border bg-surface shadow-elevation-1`,
        expanded ? `border-primary-600` : `border-default`,
        isDragging && `opacity-40`,
      )}
    >
      <div className="flex items-center gap-1 p-1.5">
        {!readOnly && (
          <span
            ref={(node) => {
              handleRef(node)
            }}
            aria-label="Drag to reorder"
            className="flex h-8 w-5 shrink-0 cursor-grab items-center justify-center text-minimal hover:text-subtle"
          >
            <DotsSixVerticalIcon size={16} weight="bold" />
          </span>
        )}
        <Button variant="ghost" size="row" aria-expanded={expanded} onClick={onToggle}>
          <span className="w-6 shrink-0 text-xs text-minimal tabular-nums">{index + 1}</span>
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="truncate text-sm font-medium text-strong">{summary}</span>
            <span className="flex flex-wrap items-center gap-1.5 text-xs text-subtle">
              {QUESTION_KIND_LABELS[question.kind]}
              {answerable && question.required && <Badge label="Required" />}
              {conditionLabel && source && (
                <Badge color="purple" label={`If question ${questions.indexOf(source) + 1} is ${conditionLabel}`} />
              )}
              {question.mappingTarget && (
                <Badge
                  color="blue"
                  label={`Saves to ${MAPPING_TARGET_LABELS[question.mappingTarget] ?? question.mappingTarget}`}
                />
              )}
            </span>
          </span>
        </Button>
      </div>

      {expanded && (
        <fieldset disabled={readOnly} className="flex flex-col gap-4 border-t border-default px-4 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Type">
              <Select
                value={question.kind}
                onValueChange={(kind) => onChange(changeKind(question, kind as QuestionKind))}
              >
                <SelectTrigger aria-label="Question type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(QUESTION_KIND_LABELS) as QuestionKind[]).map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {QUESTION_KIND_LABELS[kind]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {answerable && (
              <Field label="Required">
                <div className="flex h-9 items-center">
                  <Switch
                    aria-label="Required"
                    checked={question.required}
                    onCheckedChange={(required) => patch({ required })}
                  />
                </div>
              </Field>
            )}
          </div>

          {question.kind === `staticText` ? (
            <Field label="Text" hint="Shown to the patient as a paragraph to read, not a question.">
              <Textarea rows={5} value={question.label} onChange={(e) => patch({ label: e.target.value })} />
            </Field>
          ) : (
            <>
              <Field label="Question">
                <Input value={question.label} onChange={(e) => patch({ label: e.target.value })} />
              </Field>
              <Field label="Help text">
                <Input
                  value={question.helpText ?? ``}
                  placeholder="Optional"
                  onChange={(e) => patch({ helpText: e.target.value || undefined })}
                />
              </Field>
            </>
          )}

          {OPTION_KINDS.includes(question.kind) && (
            <Field label="Choices">
              <div className="flex flex-col gap-2">
                {(question.options ?? []).map((option, optionIndex) => (
                  <div key={option.id} className="flex items-center gap-2">
                    <Input
                      aria-label={`Choice ${optionIndex + 1}`}
                      value={option.label}
                      onChange={(e) =>
                        setOptions(
                          question.options?.map((o) => (o.id === option.id ? { ...o, label: e.target.value } : o)),
                        )
                      }
                    />
                    <Button
                      variant="subtle"
                      size="icon-sm"
                      aria-label={`Remove choice ${optionIndex + 1}`}
                      onClick={() => setOptions(question.options?.filter((o) => o.id !== option.id))}
                    >
                      <Xmark />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="subtle"
                  size="sm"
                  className="self-start"
                  onClick={() =>
                    setOptions([
                      ...(question.options ?? []),
                      { id: mintId(`opt`), label: `Option ${(question.options?.length ?? 0) + 1}` },
                    ])
                  }
                >
                  <Plus /> Add choice
                </Button>
              </div>
            </Field>
          )}

          {sources.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Show this question">
                <Select
                  value={question.showWhen?.questionId ?? NO_CONDITION}
                  onValueChange={(questionId) => {
                    const picked = sources.find((q) => q.id === questionId)
                    const first = picked && conditionValues(picked)[0]
                    patch({ showWhen: picked && first ? { questionId: picked.id, equals: first.id } : undefined })
                  }}
                >
                  <SelectTrigger aria-label="Show this question">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CONDITION}>Always</SelectItem>
                    {sources.map((q) => (
                      <SelectItem key={q.id} value={q.id}>
                        {`When "${q.label || `Untitled question`}" is`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {source && question.showWhen && (
                <Field label="Answer">
                  <Select
                    value={question.showWhen.equals}
                    onValueChange={(equals) => patch({ showWhen: { questionId: source.id, equals } })}
                  >
                    <SelectTrigger aria-label="Answer">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {conditionValues(source).map((value) => (
                        <SelectItem key={value.id} value={value.id}>
                          {value.label || `Blank choice`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
          )}

          {targets.length > 0 && (
            <Field
              label="Save answer to contact"
              info={
                question.kind === `yesNo`
                  ? `Yes turns text message consent on for the contact, and No turns it off. Rewording the question never breaks this.`
                  : `When the patient finishes, this answer updates the field on their record. Rewording the question never breaks this.`
              }
            >
              <Select
                value={question.mappingTarget ?? NO_MAPPING}
                onValueChange={(target) => patch({ mappingTarget: target === NO_MAPPING ? undefined : target })}
              >
                <SelectTrigger aria-label="Save answer to contact">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_MAPPING}>Don't save to the contact</SelectItem>
                  {targets.map((target) => (
                    <SelectItem key={target} value={target}>
                      {MAPPING_TARGET_LABELS[target]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          {!readOnly && (
            <div className="flex items-center gap-1 border-t border-default pt-3">
              <Button
                variant="subtle"
                size="icon-sm"
                aria-label="Move up"
                disabled={index === 0}
                onClick={() => onMove(index - 1)}
              >
                <ArrowUp />
              </Button>
              <Button
                variant="subtle"
                size="icon-sm"
                aria-label="Move down"
                disabled={index === questions.length - 1}
                onClick={() => onMove(index + 1)}
              >
                <ArrowDown />
              </Button>
              <Button variant="subtle" size="sm" className="ml-auto" onClick={onDuplicate}>
                <Copy /> Duplicate
              </Button>
              <Button variant="danger" size="sm" onClick={onRemove}>
                <Trash /> Delete question
              </Button>
            </div>
          )}
        </fieldset>
      )}
    </div>
  )
}
